import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment/moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { Button, DateRangePicker, FlexBox, Modal, Text } from '@eltoro-ui/components';
import InfoNote from 'Components/SummaryCart/SummaryEditModal/components/AdvancedOptions/InfoNote';
import Result from 'Pages/OrderlineDetails/components/AttachedAudiences/components/EditAudiencesModal/Result';
import type { FlightMutableDataType } from 'types';
import './GeneratedCampaign.scss';
import classNames from 'classnames';

type EditGeneratedCampaignFlightDateProps = {
  onClose: () => void;
  campaignData: any;
  cost: number;
  handleGeneratedBannerFlightDateChange: (
    startDate: Date | string,
    endDate: Date | string,
    suggestedDates: number
  ) => void;
};
const EditGeneratedCampaignFlightDate = ({
  onClose,
  campaignData,
  cost,
  handleGeneratedBannerFlightDateChange,
}: EditGeneratedCampaignFlightDateProps) => {
  const [isCalendarOpen, setCalendarOpen] = useState(false);
  const initialFlightDateData = {
    startDate: moment(campaignData.startDate).toDate(),
    endDate: moment(campaignData.endDate).toDate(),
    currentDays: campaignData.suggestedDates ?? 7,
    minDays: 7,
    days: campaignData.suggestedDates ?? 7,
  };
  const [flightMutableData, setFlightMutableData] =
    useState<FlightMutableDataType>(initialFlightDateData);
  const canConfirmEditDate = !(
    new Date(Date.now()).getDate() === new Date(flightMutableData.startDate).getDate() &&
    new Date(Date.now()).getDate() === new Date(flightMutableData.endDate).getDate()
  );

  const onStartDateChange = (newDate: Date) => {
    setFlightMutableData(prev => {
      const duration = moment(newDate).add({ days: 1 }).diff(moment(newDate), 'days');
      return {
        ...prev,
        startDate: newDate,
        endDate: newDate,
        currentDays: duration,
        days: duration >= prev.minDays ? duration : prev.days,
      };
    });
  };

  const onEndDateChange = (newDate: Date) => {
    setFlightMutableData(prev => {
      const duration = moment(newDate).add({ days: 1 }).diff(moment(prev.startDate), 'days');
      return {
        ...prev,
        endDate: newDate,
        currentDays: duration,
        days: duration >= prev.minDays ? duration : prev.days,
      };
    });
  };

  const hasError = useMemo(() => {
    return flightMutableData.currentDays < flightMutableData.minDays;
  }, [flightMutableData.currentDays, flightMutableData.minDays]);

  useEffect(() => {
    setCalendarOpen(hasError);
  }, [hasError]);
  const handleConfirm = () => {
    handleGeneratedBannerFlightDateChange(
      flightMutableData.startDate,
      flightMutableData.endDate,
      flightMutableData.days
    );
  };
  return (
    <Modal className="edit-creative-modal edit-url-modal" offClick={onClose}>
      <Button onClick={onClose} UNSAFE_className="close_cross">
        <FontAwesomeIcon icon={faTimes} style={{ cursor: 'pointer', fontSize: '24px' }} />
      </Button>
      <FlexBox flexDirection="column" alignItems="center" UNSAFE_className="budget_wrapper">
        <FlexBox
          flexDirection="column"
          UNSAFE_style={{ width: '100%', position: 'relative' }}
          gap="20px"
        >
          <Text
            on="white"
            weight="bold"
            textAlign="center"
            UNSAFE_style={{ color: '#2B1F0A', fontSize: 24 }}
            UNSAFE_className="heading"
          >
            Edit Flight Dates
          </Text>
          <FlexBox UNSAFE_className="options_wrapper">
            <FlexBox flexDirection="column" UNSAFE_className="flight_date_wrapper">
              <FlexBox
                UNSAFE_className="cost_item_wrapper"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <FlexBox
                  UNSAFE_className="heading"
                  flexDirection="column"
                  justifyContent="space-between"
                  padding="0.75rem 1.5rem"
                  UNSAFE_style={{ background: '#FEFEFE' }}
                >
                  <FlexBox justifyContent="space-between" alignItems="flex-start">
                    <Text
                      on="white"
                      weight="bold"
                      textAlign="left"
                      UNSAFE_style={{
                        color: '#2B1F0A',
                        fontSize: 16,
                        textTransform: 'capitalize',
                        marginBottom: '5px',
                      }}
                    >
                      Flight Dates
                    </Text>
                    <Text
                      on="white"
                      weight="bold"
                      textAlign="left"
                      UNSAFE_style={{ color: '#2B1F0A', fontSize: 16 }}
                    >
                      {flightMutableData.days} days
                    </Text>
                  </FlexBox>
                  <Text
                    on="white"
                    textAlign="left"
                    UNSAFE_style={{ color: '#757575', fontSize: 14 }}
                  >
                    Based on your current set budget, flight days cannot be less than{' '}
                    {flightMutableData.minDays}.
                  </Text>
                </FlexBox>
                <FlexBox UNSAFE_className="flight_date_info_wrapper">
                  <InfoNote />
                </FlexBox>
                <FlexBox
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  UNSAFE_style={{
                    background: '#FEFEFE',
                    padding: '2rem',
                    width: '100%',
                    borderTop: '1px solid #E7E7E7',
                    borderRadius: '0px 0px 12px 12px',
                  }}
                >
                  <FlexBox
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    UNSAFE_style={{ width: '100%', marginBottom: '1rem' }}
                  >
                    <input
                      className={classNames('custom_date_input', hasError && 'error')}
                      value={`${moment(flightMutableData.startDate).format(
                        'MM/DD/YYYY'
                      )} - ${moment(flightMutableData.endDate).format('MM/DD/YYYY')}`}
                      readOnly
                      onClick={() => setCalendarOpen(ps => !ps)}
                    />
                    {hasError && (
                      <Text
                        on="white"
                        textAlign="left"
                        UNSAFE_style={{ color: '#FF2D38', fontSize: 14, marginTop: '5px' }}
                      >
                        Flight days cannot be less than {7}
                      </Text>
                    )}
                  </FlexBox>
                  {isCalendarOpen && (
                    <FlexBox
                      flexDirection="column"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                    >
                      <div className="DateRangeSelector__date-selection">
                        <DateRangePicker
                          startDate={moment(flightMutableData.startDate).toDate()}
                          endDate={moment(flightMutableData.endDate).toDate()}
                          min={new Date()}
                          onStartDateChange={onStartDateChange}
                          onEndDateChange={onEndDateChange}
                        />
                      </div>
                    </FlexBox>
                  )}
                </FlexBox>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        </FlexBox>
        <Result
          cost={cost}
          closeModal={onClose}
          hasError={hasError}
          confirmGeneratedDate={() => handleConfirm()}
          submitLoading={false}
          canConfirmEditDate={canConfirmEditDate}
          isGeneratedCampaign
        />
      </FlexBox>
    </Modal>
  );
};

export default EditGeneratedCampaignFlightDate;
